import { CustomerId } from '@qmrs/entityid';
import type { ViteSSGContext } from 'vite-ssg';
import { ActivityEvent } from './graphql/types-history';
import { CodeAnlassUntersuchungType } from 'xoev-xwasser-web-101';
import {
    // ContextType as LegacyContextType,
    AuthorityExportOptions,
    AuthoritySortField,
    LaboratoryExportOptions,
    LaboratorySortField,
    Ordering,
    UserSortField,
    WaterSupplierExportOptions,
    WaterSupplierSortField
} from './graphql/types';
import {
    ContextType,
    Address,
    BasicEntityType,
    Modification,
    PublicEntityKind,
    PublicEntityType,
    WsSystemSortField
} from './graphql/types-service';
import type { ParseResult } from './workers/uploader/model';

export type UserModule = (ctx: ViteSSGContext) => void;
export type AutocompleteOption = { id: string; name: string };

export interface EntityWithDefaultFields {
    id: string;
    name: string;
}

export interface EntityWithAddress {
    address: Address;
}

export interface EntityWithModification {
    modification: Modification;
}

export interface MenuItem {
    key: string;
    title: string;
    children?: MenuItem[];
    to?: string;
    icon?: string;
}

export interface LoadedFederalState {
    key: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    id: CustomerId;
    exists?: boolean;
}

export type LoadedFederalStateList = LoadedFederalState[];

export enum UserType {
    None = 0x0000,
    Applicant = 0x0001,
    WspBranch = 0x00f1,
    Wsp = 0x00f0,
    LabBranch = 0x0ff1,
    Lab = 0x0ff0,
    Hdp = 0xf000,
    Olb = 0xf001,
    OlbBranch = 0xf002,
    Support = 0x7fff,
    Admin = 0xffff
}

export function userToContextType(userType: UserType) {
    let result: ContextType;
    switch (userType) {
        case UserType.Olb:
            result = ContextType.Olb;
            break;
        case UserType.OlbBranch:
            result = ContextType.OlbB;
            break;
        case UserType.Hdp:
            result = ContextType.Hdp;
            break;
        case UserType.Lab:
            result = ContextType.Lab;
            break;
        case UserType.LabBranch:
            result = ContextType.LabB;
            break;
        case UserType.Wsp:
            result = ContextType.Wsp;
            break;
        case UserType.WspBranch:
            result = ContextType.WspB;
            break;
        default:
            result = ContextType.Sys;
            break;
    }
    return result;
}

export function contextToUserType(contextType: ContextType) {
    let result: UserType;
    switch (contextType) {
        case ContextType.Olb:
            result = UserType.Olb;
            break;
        case ContextType.OlbB:
            result = UserType.OlbBranch;
            break;
        case ContextType.Hdp:
            result = UserType.Hdp;
            break;
        case ContextType.Lab:
            result = UserType.Lab;
            break;
        case ContextType.LabB:
            result = UserType.LabBranch;
            break;
        case ContextType.Wsp:
            result = UserType.Wsp;
            break;
        case ContextType.WspB:
            result = UserType.WspBranch;
            break;
        default:
            result = UserType.None;
            break;
    }
    return result;
}

export interface NamedRowData {
    id: string;
    name: string;
}

export interface NamedRow {
    newData: NamedRowData;
}

export interface BreadcrumbItemContext {
    label: string;
    loading: boolean;
    ty: string;
    error?: string;
}

export interface BreadcrumbItem {
    label: string;
    route?: string;
    loading?: boolean;
}

export interface UserContext {
    ty?: string;
    id: string;
}

export interface ErrorObject {
    path: string;
    message: string;
}

export interface MatchedItem {
    value: string;
    text: string;
    snippet: string;
}

export interface ItemGroup {
    label: string;
    items: Item[];
}

export interface CodeListStats {
    id: number;
    amount: number;
    label: string;
}

export interface Option {
    type: string;
    code: string;
    desc: string;
}

export interface QualityReportNavMenuItem {
    id: string;
    index: number;
    label: string;
    errors: number;
}

export interface SampeMenuItem {
    id: string;
    index: number;
    label: string;
    errors: number;
}

export interface Parameter {
    id: string;
    type: string;
    unit: string;
    expr: string | undefined;
    result: number | undefined;
    limit: number | undefined;
    methods: string[];
    methodHints: string | undefined;
    bewertungUntersuchungswert: string | undefined;
}

export interface Sample {
    id: string;
    name: string;
    sampleTool?: string;
    probengefaess?: string;
    ergaenzendeInformationenZuProbe?: string;
    desinfektionProbenentnahmegeraet?: boolean;
    shapthProbennummer: string;
    vonProbennehmerVergebeneProbeId: string;
    probeIdAusLabor: string;
    untersuchungsanlass?: CodeAnlassUntersuchungType[];
    medium?: string;
    konformitaetsbewertungProbe: string;
    ergaenzungZumMedium: string;
    terminplanId: string;
    informationenZumProbentransport: string;
    informationenZurAngeliefertenProbe: string;
    kommentar: string;
    berichtspflichtig?: boolean;
    akkreditierteDurchfuehrungDerProbennahme?: boolean;
    angelieferteProbe?: boolean;
    probennahmeverfahren: object[];
    zeitpunktProbennahme: string;
    beginnLabortaetigkeit: string;
    eingangUntersuchungsstelle: string;
    abschlussLabortaetigkeit: string;
    samplePoint: string;
    parameter: Parameter[];
    errors: number;
}

export interface QualityReportOwnerSubDetails {
    id: string;
    name: string;
}

export interface QualityReportOwnerDetails {
    id: string;
    name: string;
    ident: string;
    accreditation: string;
    street: string;
    zip: string;
    place: string;
    additional?: string;
    testAreaAccredPhPhChChTests: boolean;
    testAreaMicrobiologicalExaminations: boolean;
    testAreaInvestigationsForRadionuclides: boolean;
    testAreaOnSiteParameterOnly: boolean;
    sub: QualityReportOwnerSubDetails | null;
}

export interface ErrorObject {
    path: string;
    message: string;
}

export interface Item {
    value: string;
    text: string;
    extra?: string;
    destatis?: string;
}

export interface Code {
    code?: string;
    text?: string;
    desc?: string;
}

export interface Entity {
    r?: string;
    id?: string;
    name?: string;
    ty?: ContextType | BasicEntityType | PublicEntityType;
}

export interface FilteredParameters extends Item {
    [key: string]: string | boolean | undefined;
}

export interface CodeListStats {
    id: number;
    amount: number;
    label: string;
}

export interface Option {
    type: string;
    code: string;
    desc: string;
}

export interface MeetingItem {
    [key: string]: string | boolean | Item | undefined | null;
}

export interface ColumnConfig {
    id: string;
    label: string;
    flag?: boolean;
}

export interface ColumnSelectors {
    id: string;
    label: string;
    valueSelector: (v: any) => string;
}

export interface DocComment {
    author: string;
    content: string;
    created: Date;
}

export interface CreationDataComments {
    list: DocComment[];
    current: DocComment;
}
export interface ColumnDragEvent {
    dragIndex: number;
    dropIndex: number;
}

export interface EditNameEvent {
    value: string;
    index: number;
}

export interface ExtActivity extends ActivityEvent {
    icon: string;
    ago: string;
    dateTime: string;
}

export interface CtxItem {
    id: string;
    name: string;
}

export interface AdminUserContext {
    ty: ContextType;
    kind: PublicEntityKind;
    id: string;
    name: string;
}

export enum WaterSupplierEditionMode {
    Total = 'TOTAL',
    BetreiberConfig = 'BETREIBER_CONFIG'
}

export type SortField =
    | WaterSupplierSortField
    | AuthoritySortField
    | LaboratorySortField
    | UserSortField
    | WsSystemSortField;

export type SortEmit = (
    event: 'sort-change',
    field: SortField,
    order: Ordering
) => void;

export interface StepsData {
    name: string;
    key: string;
    grid?: boolean;
}

export interface FederalStateInfo {
    name: string;
    code: string;
    destatis: string;
}

export type ExportOptions =
    | WaterSupplierExportOptions
    | LaboratoryExportOptions
    | AuthorityExportOptions;

export enum UserStatisticsChartInterval {
    Daily = 'd',
    Weekly = 'w',
    Monthly = 'm',
    Quarterly = 'q',
    Yearly = 'y'
}

export interface XmlImportResult<T> {
    result: ParseResult<T>;
    id: string;
}
